import React from "react";
import { Link } from "react-router-dom";
import Header from "../component/header";
import BannerImg from "../image/comunity.png";
import Banner from "../component/Banner";
import Footer from "../component/Footer";
import Spotlight from "../component/Spotlight";
import Voice from "../component/community/Voice";
import Connects from "../component/community/Connects";
import Path from "../component/community/Path";
import CommunityFeedback from "../component/Community";
import Breadcrumb from "../component/Breadcrumb";
import { ArrowRight, Heart, Newspaper, ShieldCheck, Star, Users } from "lucide-react";

const Community = () => {
  return (
    <>
      <Header />
      <Banner
        backgroundImage={BannerImg}
        heading="Community"
        paragraph="Lightweave Beacon Legacy is a space for artists to connect, share, grow, and support each other. Every member belongs here."
        minHeight="100vh"
        padding="80px 0"
        bton="Members please sign-in"
      />
      <Breadcrumb items={[{ label: "Community" }]} />

      {/* URM / Community Support */}
      <section className="main-lwbl px-4 py-14">
        <div className="container m-auto">
          <div className="grid gap-8 md:grid-cols-2 items-center">
            <div>
              <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008aa0]">
                Equity & inclusion
              </p>
              <h2 className="mt-2 font-cinzel text-[32px] leading-tight text-[#101819] md:text-[40px]">
                A community built for underrepresented voices
              </h2>
              <p className="mt-4 font-outfit text-sm leading-7 text-[#536061]">
                LWBL actively creates space for artists from underrepresented and marginalized communities.
                We believe diverse perspectives strengthen the art world for everyone. Our programs,
                resources, and spotlights intentionally amplify artists who have historically been overlooked.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/membership"
                  className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-cyan-600"
                >
                  Learn about membership <ArrowRight size={14} />
                </Link>
                <Link
                  to="/donation"
                  className="inline-flex items-center gap-2 rounded-full border border-cyan-700 bg-cyan-50 px-5 py-2.5 font-outfit text-sm font-semibold text-cyan-900 hover:bg-cyan-100"
                >
                  Support URM programs <ArrowRight size={14} />
                </Link>
              </div>
            </div>
            <div className="rounded-2xl border border-cyan-100 bg-cyan-50 p-8 flex flex-col gap-4">
              {[
                { icon: ShieldCheck, text: "Safe, moderated community spaces" },
                { icon: Star, text: "URM Spotlight features in galleries and newsletters" },
                { icon: Heart, text: "Support programs and grant awards for underrepresented artists" },
                { icon: Users, text: "Mentors who reflect the community's diversity" },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-cyan-100 text-cyan-700">
                    <Icon size={16} />
                  </div>
                  <p className="font-outfit text-sm text-[#536061]">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Voice />
      <Connects />
      <Path />

      {/* Community Hub */}
      <section className="main-lwbl px-4 py-14">
        <div className="container m-auto">
          <div className="text-center mb-10">
            <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008aa0]">
              Community hub
            </p>
            <h2 className="mt-2 font-cinzel text-3xl text-[#101819]">Explore stories, feedback, and ways to get involved</h2>
            <p className="mt-4 font-outfit text-sm leading-7 text-[#536061]">
              Connect with the latest member writing, share feedback, and discover the supports that make LWBL a welcoming creative network.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                title: "Community blogs",
                description: "Read member stories, journey notes, and creative reflections from across LWBL.",
                link: "/blogs",
                button: "Browse blogs"
              },
              {
                title: "Member feedback",
                description: "See the conversations that help artists grow and connect around shared work.",
                link: "/contact",
                button: "Share your thoughts"
              },
              {
                title: "Support opportunities",
                description: "Learn how your membership or contribution expands access for underrepresented makers.",
                link: "/donation",
                button: "Support the mission"
              }
            ].map((item) => (
              <div key={item.title} className="rounded-3xl border border-gray-100 bg-white p-7 shadow-sm">
                <h3 className="font-cinzel text-xl text-[#101819]">{item.title}</h3>
                <p className="mt-4 font-outfit text-sm leading-7 text-[#536061]">{item.description}</p>
                <Link
                  to={item.link}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-cyan-600"
                >
                  {item.button} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CommunityFeedback />

      {/* Newsletter Preview CTA */}
      <section className="main-lwbl px-4 py-14">
        <div className="container m-auto">
          <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm md:grid md:grid-cols-[1.4fr_1fr]">
            <div className="flex flex-col justify-center p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700">
                  <Newspaper size={20} />
                </div>
                <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008aa0]">
                  Newsletter
                </p>
              </div>
              <h2 className="font-cinzel text-2xl text-[#101819] md:text-3xl">
                Stay connected with the Spotlight Newsletter
              </h2>
              <p className="mt-4 font-outfit text-sm leading-7 text-[#536061]">
                Weekly community updates, member stories, mentorship highlights, and practical tips for growing your art practice.
                Dive into the next issue to see who’s being celebrated, what’s coming next, and how you can join the conversation.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {[
                  "Quest spotlight: fresh challenges and entry tips",
                  "Mentor moments: advice from LWBL guides",
                  "Resources: grants, workshops, and art career insights",
                  "Community preview: member wins and upcoming events",
                ].map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm text-[#536061]">
                    {item}
                  </div>
                ))}
              </div>
              <Link
                to="/blogs"
                className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-cyan-600"
              >
                Read the latest issue <ArrowRight size={14} />
              </Link>
            </div>
            <div className="hidden md:grid gap-4 p-8 bg-cyan-50">
              {[
                {
                  title: "Member Spotlight",
                  detail: "See recent submissions chosen for community praise and gallery display."
                },
                {
                  title: "Upcoming Gatherings",
                  detail: "Find mentor sessions, workshops, and community gatherings you can join this month."
                },
              ].map((item) => (
                <div key={item.title} className="rounded-3xl bg-white p-6 shadow-sm">
                  <p className="font-outfit text-xs uppercase tracking-[0.16em] text-[#008aa0]">Preview</p>
                  <h3 className="mt-3 font-cinzel text-xl text-[#101819]">{item.title}</h3>
                  <p className="mt-2 font-outfit text-sm leading-7 text-[#536061]">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Become a Member / Supporter CTAs */}
      <section className="main-lwbl px-4 pb-16">
        <div className="container m-auto">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col justify-between rounded-3xl bg-[#071212] p-8 text-white shadow-xl">
              <div>
                <Users size={28} className="mb-4 text-cyan-300" />
                <h3 className="font-cinzel text-2xl">Become a Member</h3>
                <p className="mt-3 font-outfit text-sm leading-7 text-white/75">
                  Join LWBL to unlock full community access, curated learning paths, mentorship moments, and the ability to submit work for critique and showcase.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-white/75">
                  <li>• Personalized member pathways</li>
                  <li>• Priority access to workshops and mentor sessions</li>
                  <li>• Exclusive community showcases and quest previews</li>
                </ul>
              </div>
              <Link
                to="/membership"
                className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-cyan-400 px-5 py-2.5 font-outfit text-sm font-semibold text-black hover:bg-cyan-300"
              >
                See membership plans <ArrowRight size={14} />
              </Link>
            </div>

            <div className="flex flex-col justify-between rounded-3xl border border-gray-100 bg-white p-8 shadow-sm">
              <div>
                <Heart size={28} className="mb-4 text-pink-500" />
                <h3 className="font-cinzel text-2xl text-[#101819]">Become a Supporter</h3>
                <p className="mt-3 font-outfit text-sm leading-7 text-[#536061]">
                  Help LWBL grow its support for artists from underrepresented backgrounds. Your support funds grants, community programs, and the resources that make this space safer and more vibrant.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-[#536061]">
                  <li>• Donor-funded grants for diverse artists</li>
                  <li>• Scholarships for workshops and mentorship</li>
                  <li>• Community events that center belonging and access</li>
                </ul>
              </div>
              <Link
                to="/donation"
                className="mt-6 inline-flex items-center gap-2 self-start rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-pink-600"
              >
                Support LWBL <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Spotlight />
      <Footer />
    </>
  );
};

export default Community;
